/**
 * ADR-0185 Phase B — pure composition behind the Refine loop: the strategy
 * vocabulary the popover and the pending table share, each move's
 * plain-language breakdown (ALWAYS on the row — nothing about a move hides
 * behind Edit), the best-fit no-op rule, the dock's honest stage lines, and
 * the attribution of a landed replan in the user's terms. No DOM, no
 * fetches — unit-tested in refineMath.test.ts.
 */
import type { ApprovedMachine, BoardLaneJob, BoardProposalView, Placement, PlacementKind, VersusPrevious } from "../../boardTypes.js";
import { jobLabel, ordinal } from "./laneMath.js";

export interface StrategyDecl {
  kind: PlacementKind;
  label: string;
  /** The popover's hint after the em-dash. */
  hint: string;
  /** Needs the second picker (neighbor / date & time). */
  parameterized: boolean;
}

export const STRATEGIES: readonly StrategyDecl[] = [
  { kind: "best_fit", label: "Best fit", hint: "the solver places it, promises first", parameterized: false },
  { kind: "run_next", label: "Run next", hint: "right after the running job", parameterized: false },
  { kind: "before_after", label: "Before / after a job…", hint: "pick the neighbor", parameterized: true },
  { kind: "exact_time", label: "Exact date & time…", hint: "hard; others move around it", parameterized: true },
  { kind: "near_time", label: "Near a date…", hint: "soft; not before it, then as close as fits", parameterized: true },
];

export function strategyLabel(kind: PlacementKind): string {
  return STRATEGIES.find((s) => s.kind === kind)?.label ?? kind;
}

/** "Mon 9/8, 6:00 AM" — the short absolute form the breakdowns use. */
export function shortWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${weekday} ${d.getMonth() + 1}/${d.getDate()}, ${time}`;
}

/**
 * Jobs the plan carries, by id — the breakdown names neighbors "159981
 * (BD7042)". Carries each job's approved set (null when the Approved
 * machines card is unbound, unreadable, or the part has no rows) and the
 * verdict on its plan machine, straight off the served lane row.
 */
export type PlanJobs = Map<
  string,
  { part: string | null; machine: string; running: boolean; approved_machines: ApprovedMachine[] | null; machine_approved: boolean | null }
>;

export function planJobsOf(view: Pick<BoardProposalView, "lanes"> | null): PlanJobs {
  const map: PlanJobs = new Map();
  for (const lane of view?.lanes ?? []) {
    for (const j of lane.jobs) {
      if (!map.has(j.job)) {
        map.set(j.job, {
          part: j.part,
          machine: lane.machine,
          running: j.running,
          approved_machines: j.approved_machines ?? null,
          machine_approved: j.machine_approved ?? null,
        });
      }
    }
  }
  return map;
}

/** Is `machine` in the served set? Null when there is no set to ask (unbound, unreadable, no rows). */
export function machineInSet(approved: readonly ApprovedMachine[] | null | undefined, machine: string): boolean | null {
  if (!approved) return null;
  return approved.some((a) => a.machine === machine);
}

export interface MachineOption {
  machine: string;
  label: string;
  disabled: boolean;
  reason?: string;
}

/** The unbound help line — the pre-amendment sentence, unchanged. */
export const MACHINE_HELP_UNBOUND =
  "Moving machines is your call — you know what it can run. Run hours stay the book's figure and are flagged as assumed on a new machine.";

/**
 * The move popover's machine select, as a pure function of what the board
 * served (Approved machines, spec §6.1). Four states:
 *   unbound            → every lane on the view, the plan machine marked (stay) — today's behaviour;
 *   bound, set present → the approved machines in preference order, the plan machine first-or-marked
 *                        ("(stay — not approved here)" when it is outside the set: staying is not a move),
 *                        approved machines that are no lane on this board listed disabled;
 *   bound, no rows     → the plan machine alone, the select disabled, the sentence says why;
 *   bound, unreadable  → as no rows, with the served reason as the help line (moves are refused hub-side too).
 */
export function machineOptionsFor(args: {
  part: string | null;
  approved: readonly ApprovedMachine[] | null | undefined;
  planMachine: string;
  viewMachines: readonly string[];
  bound: boolean;
  /** Served when the card is bound but its dataset can't be read right now. */
  unavailableReason?: string | null;
}): { options: MachineOption[]; help: string; selectDisabled: boolean } {
  const name = args.part ?? "this part";
  const stay = (label = "(stay)"): MachineOption => ({ machine: args.planMachine, label: `${args.planMachine} ${label}`, disabled: false });
  if (!args.bound) {
    return {
      options: args.viewMachines.map((m) => ({ machine: m, label: m === args.planMachine ? `${m} (stay)` : m, disabled: false })),
      help: MACHINE_HELP_UNBOUND,
      selectDisabled: false,
    };
  }
  if (args.unavailableReason) {
    return {
      options: [stay()],
      help: `The approved-machines dataset can't be read right now — ${args.unavailableReason}. Machine moves wait until it can.`,
      selectDisabled: true,
    };
  }
  const approved = args.approved ?? null;
  if (!approved || approved.length === 0) {
    return {
      options: [stay()],
      help: `No approved machines on record for ${name} — it can't be moved to another machine.`,
      selectDisabled: true,
    };
  }
  const planInSet = approved.some((a) => a.machine === args.planMachine);
  const options: MachineOption[] = [planInSet ? stay() : stay("(stay — not approved here)")];
  for (const a of approved) {
    if (a.machine === args.planMachine) continue;
    options.push(
      a.on_board
        ? { machine: a.machine, label: a.machine, disabled: false }
        : { machine: a.machine, label: `${a.machine} · not on this board`, disabled: true, reason: "not on this board" }
    );
  }
  const help =
    approved.length === 1
      ? `${name} runs only on ${approved[0]!.machine}.`
      : `Only machines the routing approves for ${name}. Run hours stay the book's figure and are flagged as assumed on a new machine.`;
  return { options, help, selectDisabled: false };
}

/**
 * The popover title's disclosure of the set — the ALTERNATIVES only:
 * "also runs on C13/H36". Owner's ruling, 2026-09-21: the title already says
 * where the job is ("· on C12/H32"), so naming that machine again in the same
 * line was repetition. What the planner does not already know is where ELSE it
 * could go, and that is what the picker is about to offer.
 *
 * Null when there is nothing to add: no set at all, or a set whose only
 * machine is the one the job is already on. A single-machine part therefore
 * shows no clause here — the select's own help line says "BD4182 runs only on
 * C12/H32", one line below, and saying it twice is the same repetition again.
 */
export function canRunOnLine(
  approved: readonly ApprovedMachine[] | null | undefined,
  planMachine: string
): string | null {
  if (!approved || approved.length === 0) return null;
  const others = approved.map((a) => a.machine).filter((m) => m !== planMachine);
  return others.length === 0 ? null : `also runs on ${others.join(", ")}`;
}

function nameOf(job: string, jobs: PlanJobs): string {
  const known = jobs.get(job);
  return known ? jobLabel({ job, part: known.part }) : job;
}

/**
 * The full plain-language breakdown of one pending move — every parameter
 * spelled out on the row. `otherRunNext` lists other jobs also set to run
 * next on the same machine (the front group the solver orders within).
 */
export function moveBreakdown(move: Placement, jobs: PlanJobs, pending: readonly Placement[] = []): string {
  const plan = jobs.get(move.job);
  const machine = move.machine ?? plan?.machine ?? "—";
  const moved = !!move.machine && move.machine !== plan?.machine;
  // Approved machines — the visible tell for a withdrawal AFTER the move was
  // queued: the machine has left the part's served set (bound + set present
  // + machine outside it), so the replan will refuse this move by name.
  const withdrawn = !!move.machine && machineInSet(plan?.approved_machines, move.machine) === false;
  const machineNote =
    (moved ? ` — moving to ${machine}; run hours assumed from ${plan?.machine ?? "its usual machine"}'s figure` : "") +
    (withdrawn ? " — no longer approved by the routing; replan will refuse this move" : "");
  switch (move.kind) {
    case "best_fit":
      return `solver places it on ${machine} — promises first, then setup savings${machineNote}`;
    case "run_next": {
      const others = pending
        .filter((p) => p.job !== move.job && p.kind === "run_next" && (p.machine ?? jobs.get(p.job)?.machine) === machine)
        .map((p) => nameOf(p.job, jobs));
      const group = others.length > 0 ? ` (with ${others.join(", ")} also set to run next — the solver orders that group)` : "";
      return `right after the running job on ${machine}${group}${machineNote}`;
    }
    case "before_after":
      return `${move.side === "before" ? "right before" : "right after"} ${nameOf(move.anchor_job ?? "", jobs)} on ${machine}${machineNote}`;
    case "exact_time":
      return `${move.at ? shortWhen(move.at) : "—"} on ${machine} — hard; others move around it${machineNote}`;
    case "near_time":
      return `on or after ${move.at ? shortWhen(move.at) : "—"} on ${machine} — soft; as soon after as fits well${machineNote}`;
  }
}

/**
 * The best-fit no-op rule (owner-ruled): grayed with "already at its best
 * fit" when the move would change nothing — the Refined row IS the optimized
 * plan, the machine stays, and no other move is pending.
 */
export function bestFitIsNoOp(args: {
  basis: "optimized" | "plex" | "refined";
  job: string;
  machineChoice: string;
  planMachine: string;
  pending: readonly Placement[];
}): boolean {
  if (args.basis !== "optimized") return false;
  if (args.machineChoice !== args.planMachine) return false;
  return !args.pending.some((p) => p.job !== args.job);
}

/** The dock's stage line — honest to the worker's stamp, framed with the real expectation. */
export function stageLine(stage: string | null | undefined, elapsedS: number): { text: string; fraction: number } {
  const suffix = ` · ${elapsedS}s (usually 30–45 s)`;
  switch (stage) {
    case "reading_inputs":
      return { text: `Reading the book${suffix}`, fraction: 0.2 };
    case "solving":
      return { text: `Solving${suffix}`, fraction: 0.6 };
    case "pricing":
      return { text: `Pricing the changes${suffix}`, fraction: 0.9 };
    default:
      return { text: `Waiting for the solver${suffix}`, fraction: 0.05 };
  }
}

export interface ReplanAttribution {
  /** "Your 2 moves:" then one line per move, in the user's terms. */
  yourMoves: string[];
  /** The collateral, attributed: other jobs moved / machine changes / downstream count. */
  aroundThem: string;
  /** Setup hours and promises, before → after. */
  totals: string;
  /** The baseline the diff is pinned to. */
  baseline: string;
}

function findJob(view: BoardProposalView, job: string): { job: BoardLaneJob; machine: string } | null {
  for (const lane of view.lanes) {
    const found = lane.jobs.find((j) => j.job === job);
    if (found) return { job: found, machine: lane.machine };
  }
  return null;
}

/**
 * What the replan did, attributed: the user's own moves first (where each
 * placed job landed, with its changeover repricing when the sheet knows it),
 * then everything that moved around them, then the totals — all against
 * the plan the Refined row showed before (the versus-previous baseline).
 */
export function replanAttribution(landed: BoardProposalView, baselineLabel: string): ReplanAttribution {
  const vp: VersusPrevious | null = landed.versus_previous;
  const placed = new Set(landed.placements.map((p) => p.job));
  const jobs = planJobsOf(landed);

  const yourMoves = landed.placements.map((p) => {
    const hit = findJob(landed, p.job);
    const name = nameOf(p.job, jobs);
    if (!hit) return `${name} — not in this plan`;
    const moved = vp?.jobs_moved.find((m) => m.job === p.job);
    const sheetRow = landed.change_sheet?.rows.find((r) => r.job === p.job);
    const parts: string[] = [];
    if (hit.job.machine_moved_from) parts.push(`moved ${hit.job.machine_moved_from} → ${hit.machine}`);
    else if (moved && moved.from_position !== moved.to_position) parts.push(`now runs ${ordinal(moved.to_position + 1)} on ${hit.machine} (was ${ordinal(moved.from_position + 1)})`);
    else if (hit.job.proposed_rank != null) parts.push(`runs ${ordinal(hit.job.proposed_rank)} on ${hit.machine}`);
    if (hit.job.start) parts.push(`starts ${shortWhen(hit.job.start)}`);
    const from = sheetRow?.changeover_from?.setup_hours;
    const to = sheetRow?.changeover_to?.setup_hours;
    if (typeof from === "number" && typeof to === "number" && from !== to) parts.push(`changeover ${from} → ${to} h`);
    return `${name} — ${parts.join(", ")}`;
  });

  let aroundThem: string;
  if (!vp) {
    aroundThem = "No earlier plan to compare against.";
  } else {
    const collateral = vp.jobs_moved.filter((m) => !placed.has(m.job));
    const machineChanges = collateral.filter((m) => m.from_machine !== m.to_machine).length;
    const sameMachine = collateral.length - machineChanges;
    if (collateral.length === 0) aroundThem = "Nothing else changed.";
    else {
      const bits = [`${collateral.length} other job${collateral.length === 1 ? "" : "s"} moved`];
      if (sameMachine > 0) bits.push(`${sameMachine} re-ordered on the same machine`);
      if (machineChanges > 0) bits.push(`${machineChanges} changed machines`);
      aroundThem = `Around them: ${bits.join(" · ")}.`;
    }
  }

  const setupAfter = landed.diff?.setup_hours_proposed;
  const setupDelta = vp?.setup_hours_delta;
  const risk = landed.promise_risk.count;
  const totalBits: string[] = [];
  if (typeof setupAfter === "number") {
    totalBits.push(
      typeof setupDelta === "number" && setupDelta !== 0
        ? `setup hours ${Math.round((setupAfter - setupDelta) * 10) / 10} → ${setupAfter}`
        : `setup hours ${setupAfter}`
    );
  }
  if (vp) {
    const before = risk - vp.promises_newly_at_risk.length + vp.promises_cleared.length;
    totalBits.push(before === risk ? `promises at risk ${risk}` : `promises at risk ${before} → ${risk}`);
  } else totalBits.push(`promises at risk ${risk}`);

  return { yourMoves, aroundThem, totals: totalBits.join(" · "), baseline: baselineLabel };
}
